/**
 * ShareablePass.js
 *
 * A boarding pass guests can share: the same pass as a PDF, with the seat and origin hidden,
 * so nobody has to post their real one. Drawn off screen at full size only while downloading.
 */

import React, { useEffect, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { jsPDF } from "jspdf";
import toast from "react-hot-toast";
import BoardingPass from "./BoardingPass";

const MASK = "•••";
const BACKDROP = "#E9E4DA";

// The drawing's width at full size: the wide pass, or the phone pass
const WIDE_WIDTH = 1040;
const MOBILE_WIDTH = 358;

/**
 * @param {Object} pass     -   { pnr, fullName, seat, fromCode, fromCity, colour }, from the backend (undefined until it loads)
 * @param {Boolean} mobile  -   Use the phone pass, like the one on screen
 * @returns {Object}        -   { download: starts the download, drawing: render it anywhere on the page }
 */
export const useShareablePass = (pass, mobile) => {
    const [drawing, setDrawing] = useState(false);
    const ref = useRef(null);

    // Once the hidden pass is on the page, turn it into a PDF the size of the pass and download it
    useEffect(() => {
        if (!drawing) return;
        const save = async () => {
            try {
                await document.fonts.ready;
                const { offsetWidth: width, offsetHeight: height } = ref.current;
                const image = await toPng(ref.current, { pixelRatio: 2, backgroundColor: BACKDROP });
                const pdf = new jsPDF({ orientation: width > height ? "landscape" : "portrait", unit: "px", format: [width, height], hotfixes: ["px_scaling"] });
                pdf.addImage(image, "PNG", 0, 0, width, height);
                pdf.save(`YJ030-boarding-pass-${pass.fullName.replace(/\s+/g, "-")}.pdf`);
            } catch (err) {
                console.error("Error drawing the shareable boarding pass:", err);
                toast.error("Couldn't download the boarding pass.");
            } finally {
                setDrawing(false);
            }
        };
        save();
    }, [drawing, pass?.fullName]);

    const hidden = { ...pass, seat: MASK, fromCode: MASK, fromCity: MASK };

    return {
        download: () => setDrawing(true),
        drawing: drawing && pass && (
            <div style={{ position: "fixed", left: -10000, top: 0 }} aria-hidden="true">
                <div ref={ref} style={{ width: mobile ? MOBILE_WIDTH : WIDE_WIDTH }}>
                    <BoardingPass pass={hidden} layout={{ mobile, scale: 1 }} />
                </div>
            </div>
        ),
    };
};
